import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, BookOpen } from "lucide-react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";

export default function KnowledgeBase() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState({ question: "", answer: "", category: "general" });

  const load = async () => {
    const r = await api.get("/kb");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm({ question: "", answer: "", category: "general" }); };
  const startEdit = (it) => { setEditing(it.id); setForm({ question: it.question, answer: it.answer, category: it.category || "general" }); };
  const cancel = () => { setEditing(null); };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) return;
    if (editing === "new") await api.post("/kb", form);
    else await api.put(`/kb/${editing}`, form);
    setEditing(null);
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm(t("kb.delete") + "?")) return;
    await api.delete(`/kb/${id}`);
    await load();
  };

  return (
    <div className="space-y-6" data-testid="kb-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="chip"><BookOpen className="h-3 w-3" /> {t("kb.title")}</div>
          <h1 className="mt-3 text-3xl lg:text-4xl font-extrabold text-white tracking-tight">{t("kb.title")}</h1>
          <p className="mt-1 text-slate-400 text-sm">{t("kb.sub")}</p>
        </div>
        <button onClick={startNew} className="btn-primary inline-flex items-center gap-2" data-testid="kb-add"><Plus className="h-4 w-4" /> {t("kb.add")}</button>
      </div>

      {editing && (
        <div className="glass rounded-2xl p-6 space-y-4 glow-border" data-testid="kb-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("kb.question")}</label>
              <input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} className="input-dark mt-1.5" data-testid="kb-question" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("kb.category")}</label>
              <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark mt-1.5" data-testid="kb-category" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-bold">{t("kb.answer")}</label>
            <textarea rows={4} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} className="input-dark mt-1.5 resize-none" data-testid="kb-answer" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={cancel} className="btn-ghost inline-flex items-center gap-2" data-testid="kb-cancel"><X className="h-4 w-4" /> {t("kb.cancel")}</button>
            <button onClick={save} className="btn-primary inline-flex items-center gap-2" data-testid="kb-save"><Save className="h-4 w-4" /> {t("kb.save")}</button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.length === 0 && <div className="glass rounded-2xl p-10 text-center text-slate-500 col-span-full">{t("kb.empty")}</div>}
        {items.map((it) => (
          <div key={it.id} className="glass rounded-2xl p-6 hover:-translate-y-0.5 transition" data-testid={`kb-item-${it.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-widest text-magenta">{it.category}</div>
                <div className="mt-1 font-bold text-white">{it.question}</div>
                <div className="mt-2 text-sm text-slate-400 leading-relaxed">{it.answer}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => startEdit(it)} className="p-2 rounded-lg border border-white/5 hover:border-primary/40 text-slate-400 hover:text-white transition" data-testid={`kb-edit-${it.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                <button onClick={() => remove(it.id)} className="p-2 rounded-lg border border-white/5 hover:border-rose-400/40 text-slate-400 hover:text-rose-400 transition" data-testid={`kb-del-${it.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
